import React, { useState, useEffect } from "react";
import { FiDownload, FiX, FiShare, FiPlusSquare, FiCheckCircle } from "react-icons/fi";
import "./PWAInstallPrompt.css";

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed / standalone
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isIosDevice);

    // Check if user dismissed recently
    const dismissedTime = localStorage.getItem("magizh_pwa_dismissed");
    if (dismissedTime) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedTime, 10)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // Android / Desktop beforeinstallprompt handler
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Show banner after 3 seconds for iOS if not installed
    if (isIosDevice && !isStandalone) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSModal(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === "accepted") {
      console.log("[PWA] User accepted installation");
      setIsInstalled(true);
    } else {
      console.log("[PWA] User dismissed installation");
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSModal(false);
    localStorage.setItem("magizh_pwa_dismissed", Date.now().toString());
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <>
      {/* Floating Bottom Banner */}
      <div className="pwa-install-banner animate-fade-in" role="banner">
        <div className="pwa-install-content">
          <img
            src={`${process.env.PUBLIC_URL || ""}/app-icon-192.png`}
            alt="Magizh One App Icon"
            className="pwa-app-icon"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `${process.env.PUBLIC_URL || ""}/Assets/logooo.png`;
            }}
          />
          <div className="pwa-text">
            <h4 className="pwa-title">Install Magizh One App</h4>
            <p className="pwa-subtitle">
              Get quick access, offline support, and desktop/mobile app experience!
            </p>
          </div>
        </div>

        <div className="pwa-actions">
          <button className="pwa-btn-install" onClick={handleInstallClick}>
            <FiDownload className="pwa-btn-icon" />
            <span>Install</span>
          </button>
          <button
            className="pwa-btn-dismiss"
            onClick={handleDismiss}
            aria-label="Close install prompt"
          >
            <FiX />
          </button>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSModal && (
        <div className="pwa-ios-modal-overlay" onClick={handleDismiss}>
          <div className="pwa-ios-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pwa-ios-close" onClick={handleDismiss}>
              <FiX />
            </button>
            <img
              src={`${process.env.PUBLIC_URL || ""}/app-icon-192.png`}
              alt="Magizh One"
              className="pwa-ios-logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `${process.env.PUBLIC_URL || ""}/Assets/logooo.png`;
              }}
            />
            <h3>Install Magizh One on iOS</h3>
            <p>Follow these quick steps to add the app to your Home Screen:</p>

            <ol className="pwa-ios-steps">
              <li>
                <span className="step-num">1</span>
                <span>Tap the <strong>Share</strong> button <FiShare className="inline-icon" /> at the bottom of Safari.</span>
              </li>
              <li>
                <span className="step-num">2</span>
                <span>Scroll down and select <strong>Add to Home Screen</strong> <FiPlusSquare className="inline-icon" />.</span>
              </li>
              <li>
                <span className="step-num">3</span>
                <span>Tap <strong>Add</strong> in the top-right corner.</span>
              </li>
            </ol>

            <button className="pwa-ios-done-btn" onClick={handleDismiss}>
              <FiCheckCircle /> Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
}
